import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import HttpService from "../../services/httpService";
import { setSnackInfo } from "../../redux/actions/appAction";
import {
    Grid,
    Paper,
    Card,
    CardContent,
    TextField,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    TablePagination,
    Chip,
    IconButton,
    LinearProgress,
    CircularProgress,
    InputAdornment,
    Tooltip as MuiTooltip,
    withStyles,
} from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";
import { Button } from "antd";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import TuneIcon from "@material-ui/icons/Tune";
import RefreshIcon from "@material-ui/icons/Refresh";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import GetAppIcon from "@material-ui/icons/GetApp";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import DateRangeOutlinedIcon from "@material-ui/icons/DateRangeOutlined";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import SendOutlinedIcon from "@material-ui/icons/SendOutlined";
import ReplyOutlinedIcon from "@material-ui/icons/ReplyOutlined";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import TimerOutlinedIcon from "@material-ui/icons/TimerOutlined";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import RepeatOutlinedIcon from "@material-ui/icons/RepeatOutlined";
import InboxOutlinedIcon from "@material-ui/icons/InboxOutlined";
import moment from "moment";
import XLSX from "xlsx";
import BotWotChatDrawer from "../../components/BotWotChatDrawer";
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
} from "chart.js";
import { Doughnut } from "react-chartjs-2";

ChartJS.register(ArcElement, Tooltip, Legend);

// WhatsApp-family palette, kept distinct from the orange Move-to-Marketing
// dashboard so the two are never mistaken for each other at a glance.
const WA_DARK = "#0b6b58";
const WA = "#128C7E";
const WA_LIGHT = "#25D366";
const HEADER_GRADIENT = `linear-gradient(135deg, ${WA_DARK} 0%, ${WA} 60%, ${WA_LIGHT} 100%)`;

const SENT_COLOR = "#7b8494";
const REPLIED_COLOR = "#128C7E";
const REAL_COLOR = "#0d9488";
const HOLD_COLOR = "#f59e0b";
const JUNK_COLOR = "#e11d48";
const NEUTRAL_COLOR = "#9aa4b4";

const OUTCOME_META = {
    Real: { label: "Qualified (Real)", color: REAL_COLOR },
    Hold: { label: "Hold", color: HOLD_COLOR },
    Junk: { label: "Junk", color: JUNK_COLOR },
    "Not applied": { label: "Not applied", color: NEUTRAL_COLOR },
};
const OUTCOME_ORDER = ["Real", "Hold", "Junk", "Not applied"];

const DEFAULT_FILTERS = {
    from: moment().subtract(29, "days").format("YYYY-MM-DD"),
    to: moment().format("YYYY-MM-DD"),
};

const cellBorder = { borderBottom: "1px solid #edf0f5", padding: "11px 14px" };

// Single definition of the lead table's columns, used by the real header AND
// the loading skeleton. They drifted apart once already (the skeleton kept
// rendering 8 cells after the table grew to 10) - sharing the list makes that
// impossible rather than something to remember.
const TABLE_COLUMNS = [
    { label: "Lead" },
    { label: "Customer" },
    { label: "Texts", align: "right" },
    { label: "Replies", align: "right" },
    { label: "Lead created" },
    { label: "Lead assigned" },
    { label: "First texted" },
    { label: "First reply" },
    { label: "Outcome" },
    { label: "Current Stage" },
];

// Indeterminate bar shown inside whichever card is refreshing.
const CardLinearProgress = withStyles(() => ({
    root: { height: 4, borderRadius: 999, backgroundColor: "rgba(18,140,126,0.12)" },
    bar: { borderRadius: 999, background: `linear-gradient(90deg, ${WA_DARK}, ${WA}, ${WA_LIGHT})` },
}))(LinearProgress);

// Determinate bar for the funnel steps, the outcome split and the rate tiles.
// MUI v4 gives no per-instance way to colour the inner bar, so the bar is
// painted with `currentColor` and each instance sets `color` on the root
// inline - the one approach that needs neither a class per colour nor a
// styled-components dependency.
const MeterBarBase = withStyles(() => ({
    root: { height: 8, borderRadius: 999, backgroundColor: "#EEF2F7" },
    bar: { borderRadius: 999, backgroundColor: "currentColor", transition: "transform .5s ease" },
}))(LinearProgress);

// Values are clamped: a malformed percentage would otherwise render as a bar
// overflowing its track rather than a full one.
const MeterBar = ({ value, color, height }) => (
    <MeterBarBase
        variant="determinate"
        value={Math.max(0, Math.min(100, Number(value) || 0))}
        style={{ color, ...(height ? { height } : null) }}
    />
);

const useStyles = makeStyles(() => ({
    pageRoot: {
        background: "linear-gradient(180deg, #F7F9FC 0%, #EFF3F9 100%)",
        minHeight: "100vh",
        padding: 16,
    },
    pageHeaderBanner: {
        position: "relative",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        gap: 16,
        padding: "20px 26px",
        borderRadius: 18,
        marginBottom: 14,
        background: HEADER_GRADIENT,
        boxShadow: "0 12px 30px rgba(11,107,88,0.26)",
        // Soft light bloom in the top-right corner so the banner reads as lit
        // rather than a flat gradient block.
        "&::after": {
            content: '""',
            position: "absolute",
            top: -80,
            right: -30,
            width: 260,
            height: 260,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(255,255,255,0.24) 0%, rgba(255,255,255,0) 70%)",
            pointerEvents: "none",
        },
        "& > *": { position: "relative", zIndex: 1 },
    },
    pageHeaderIconWrap: {
        width: 50,
        height: 50,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        background: "rgba(255,255,255,0.16)",
        border: "2px solid rgba(255,255,255,0.3)",
        color: "#fff",
    },
    pageHeaderTitle: { fontSize: 20, fontWeight: 700, color: "#fff", letterSpacing: 0.2 },
    pageHeaderSubtitle: { fontSize: 13, color: "rgba(255,255,255,0.75)", marginTop: 4 },
    pageHeaderRight: { marginLeft: "auto", display: "flex", alignItems: "center", gap: 12 },
    // Same contrast problem as the refresh button - a translucent WHITE fill
    // vanishes over the bright end of the banner gradient, so this darkens the
    // backdrop instead of lightening it.
    pageHeaderChip: {
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "7px 16px",
        borderRadius: 20,
        background: "rgba(0,0,0,0.42)",
        border: "1px solid rgba(255,255,255,0.22)",
        color: "#fff",
        fontSize: 13,
        fontWeight: 600,
        whiteSpace: "nowrap",
    },
    // A labelled pill, not a bare icon. The icon alone was easy to miss and
    // gave no clue that the page holds a snapshot you can re-pull.
    //
    // SOLID white, not a translucent overlay: the banner gradient ends on
    // WA_LIGHT (#25D366) and carries a white radial bloom in its top-right
    // corner - which is exactly where this sits. A 16%-white fill there was
    // near-invisible. Solid white with dark-green text reads against both the
    // dark left end and the bright right end.
    refreshButton: {
        display: "inline-flex",
        alignItems: "center",
        gap: 7,
        padding: "8px 16px",
        borderRadius: 999,
        border: "none",
        background: "#fff",
        color: WA_DARK,
        fontSize: 12.5,
        fontWeight: 800,
        letterSpacing: 0.2,
        fontFamily: "inherit",
        cursor: "pointer",
        // The 1px dark ring is what defines the button's EDGE. White-on-bright-
        // green is only ~2:1, so without it the pill's boundary dissolves into
        // the right end of the gradient even though its label is perfectly
        // readable.
        boxShadow: "0 0 0 1px rgba(0,0,0,0.14), 0 3px 10px rgba(0,0,0,0.24)",
        transition: "background .18s ease, transform .18s ease, box-shadow .18s ease",
        "&:hover:not(:disabled)": {
            background: "#F2FBF7",
            transform: "translateY(-1px)",
            boxShadow: "0 0 0 1px rgba(0,0,0,0.18), 0 6px 16px rgba(0,0,0,0.3)",
        },
        "&:active:not(:disabled)": { transform: "translateY(0)" },
        "&:disabled": { cursor: "wait", opacity: 0.85 },
    },
    refreshSpinning: { animation: "$spin 0.9s linear infinite" },
    "@keyframes spin": { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } },
    "@keyframes pulseDot": {
        "0%, 100%": { opacity: 1, transform: "scale(1)" },
        "50%": { opacity: 0.45, transform: "scale(0.82)" },
    },
    // Darkened pill rather than plain white text: white-on-#25D366 is weak,
    // and this sits over the bright end of the gradient.
    lastUpdatedText: {
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontSize: 11,
        fontWeight: 600,
        color: "#fff",
        background: "rgba(0,0,0,0.42)",
        padding: "3px 10px",
        borderRadius: 999,
        // No marginTop/marginLeft any more: this now sits beside the Refresh
        // button rather than under it, and the row's own gap does the spacing.
        whiteSpace: "nowrap",
    },
    liveDot: {
        width: 6,
        height: 6,
        borderRadius: "50%",
        flexShrink: 0,
        background: "#7CFFB2",
        animation: "$pulseDot 2s ease-in-out infinite",
    },

    filterPaper: {
        padding: "14px 18px",
        borderRadius: 14,
        marginBottom: 14,
        border: "1px solid #eaeff6",
        boxShadow: "0 2px 14px rgba(28,55,93,0.05)",
    },
    filterHeader: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        color: "#1f2d3d",
        fontWeight: 700,
        fontSize: 14,
        marginBottom: 12,
    },
    filterRow: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12 },
    field: { minWidth: 160 },
    presetGroup: { display: "flex", gap: 6, marginLeft: "auto" },
    presetChip: { cursor: "pointer", fontWeight: 600, borderRadius: 8 },
    applyButton: {
        background: WA,
        borderColor: WA,
        fontWeight: 600,
        borderRadius: 8,
        "&:hover, &:focus": { background: WA_DARK, borderColor: WA_DARK },
    },

    // Stat tiles. The accent bar on the left is set inline per tile so the
    // row reads as a set rather than six unrelated boxes.
    statCard: {
        position: "relative",
        borderRadius: 14,
        border: "1px solid #eaeff6",
        boxShadow: "0 2px 14px rgba(28,55,93,0.05)",
        overflow: "hidden",
        height: "100%",
        transition: "transform 0.15s ease, box-shadow 0.2s ease",
        "&:hover": { transform: "translateY(-2px)", boxShadow: "0 10px 24px rgba(28,55,93,0.1)" },
    },
    statAccent: { position: "absolute", left: 0, top: 0, bottom: 0, width: 4 },
    statContent: { padding: "14px 16px 15px 19px !important" },
    statTop: { display: "flex", alignItems: "center", gap: 8, marginBottom: 7 },
    // Tinted disc behind the tile icon - gives each tile a colour anchor
    // instead of a loose glyph floating next to the label.
    statIconWrap: {
        width: 28,
        height: 28,
        borderRadius: 9,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    },
    statLabel: {
        fontSize: 11.5,
        fontWeight: 700,
        letterSpacing: 0.5,
        textTransform: "uppercase",
        color: "#8b93a6",
    },
    statValue: { fontSize: 27, fontWeight: 700, color: "#1f2d3d", lineHeight: 1.1 },
    statHint: { fontSize: 12, color: "#8b93a6", marginTop: 4 },

    panelCard: {
        borderRadius: 16,
        border: "1px solid #eaeff6",
        boxShadow: "0 2px 16px rgba(28,55,93,0.06)",
        height: "100%",
        transition: "box-shadow 0.25s ease",
        "&:hover": { boxShadow: "0 10px 28px rgba(28,55,93,0.11)" },
    },
    panelTitle: {
        display: "flex",
        alignItems: "center",
        gap: 9,
        color: "#1f2d3d",
        fontWeight: 700,
        fontSize: 15,
        marginBottom: 3,
        "&::before": {
            content: '""',
            width: 4,
            height: 16,
            borderRadius: 3,
            flexShrink: 0,
            background: `linear-gradient(180deg, ${WA_LIGHT}, ${WA_DARK})`,
        },
    },
    panelHint: { color: "#8b93a6", fontSize: 12.5, marginBottom: 12, paddingLeft: 13 },
    donutWrapWide: { height: 230, position: "relative" },
    donutCenter: {
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        textAlign: "center",
        pointerEvents: "none",
    },
    donutCenterValue: { fontSize: 30, fontWeight: 800, color: WA_DARK, lineHeight: 1 },
    donutCenterLabel: { fontSize: 11, color: "#9aa4b4", marginTop: 4, letterSpacing: "0.04em" },

    // Signpost between the charts and the table.
    jumpToTable: {
        display: "flex",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
        marginTop: 14,
        padding: "12px 18px",
        borderRadius: 12,
        cursor: "pointer",
        background: "linear-gradient(135deg, #F0FAF6 0%, #E8F5F1 100%)",
        border: `1px solid ${WA}33`,
        transition: "box-shadow .18s ease, transform .18s ease",
        "&:hover": { boxShadow: `0 6px 18px ${WA}26`, transform: "translateY(-1px)" },
    },
    jumpToTableText: { fontSize: 13, fontWeight: 600, color: WA_DARK, flex: 1, minWidth: 0 },
    jumpToTableCta: {
        display: "inline-flex",
        alignItems: "center",
        gap: 3,
        padding: "5px 13px",
        borderRadius: 999,
        background: `linear-gradient(135deg, ${WA_DARK}, ${WA})`,
        color: "#fff",
        fontSize: 12,
        fontWeight: 700,
        whiteSpace: "nowrap",
    },

    legendList: { display: "flex", flexDirection: "column", gap: 8, marginTop: 4 },
    // The row's background is filled proportionally to the share (set inline),
    // so the legend doubles as a mini bar chart.
    legendRow: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "9px 13px",
        borderRadius: 11,
        border: "1px solid #eaeff6",
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
        "&:hover": { transform: "translateX(2px)", boxShadow: "0 3px 10px rgba(28,55,93,0.09)" },
    },
    legendDot: { width: 11, height: 11, borderRadius: "50%", flexShrink: 0 },
    legendLabel: { fontSize: 13, fontWeight: 600, color: "#4a5568", flex: 1 },
    legendValue: { fontSize: 14, fontWeight: 700, color: WA_DARK },
    legendPct: { fontSize: 11.5, color: "#8b93a6", minWidth: 42, textAlign: "right", fontWeight: 600 },

    tableCard: {
        borderRadius: 16,
        border: "1px solid #eaeff6",
        boxShadow: "0 2px 16px rgba(28,55,93,0.06)",
        marginTop: 14,
    },
    tableHint: {
        display: "flex",
        alignItems: "center",
        gap: 6,
        color: "#6b7689",
        fontSize: 12,
        marginBottom: 12,
        padding: "8px 12px",
        background: "#F7F9FC",
        border: "1px solid #eaeff6",
        borderRadius: 9,
    },
    headCell: {
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.07em",
        textTransform: "uppercase",
        // `!important` because MUI's own stickyHeader rule sets a plain white
        // background on th and would otherwise paint over the gradient.
        background: `linear-gradient(135deg, ${WA_DARK} 0%, ${WA} 100%) !important`,
        color: "#EAF7F3",
        border: "none",
        padding: "12px 14px",
        whiteSpace: "nowrap",
    },
    bodyCell: { ...cellBorder, fontSize: 13, color: "#37445c", whiteSpace: "nowrap" },
    // Zebra striping plus a coloured left edge that appears on hover - the row
    // lifts slightly so scanning a long list has some feedback.
    bodyRow: {
        transition: "background .15s ease, box-shadow .15s ease",
        "&:nth-of-type(even)": { background: "#FBFCFE" },
        "&:hover": {
            background: "#F1FAF7",
            boxShadow: `inset 3px 0 0 ${WA_LIGHT}`,
        },
    },
    outcomeChip: { height: 22, fontSize: 11.5, fontWeight: 600, borderRadius: 7 },

    // Initial-badge beside the customer name. Gives each row an anchor point
    // so the eye has something to track down the column.
    avatar: {
        width: 26,
        height: 26,
        borderRadius: "50%",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 10.5,
        fontWeight: 800,
        letterSpacing: 0.2,
        flexShrink: 0,
        color: "#fff",
    },
    customerCell: { display: "flex", alignItems: "center", gap: 9, minWidth: 0 },
    // Customer is the only free-text column in this table, and bodyCell is
    // whiteSpace:nowrap - so one long name stretched the column and skewed
    // every other column in the row. Capped and ellipsised instead; the full
    // name is still readable via the tooltip on hover.
    //
    // minWidth:0 is required: a flex child defaults to min-width:auto, which
    // refuses to shrink below its content and makes text-overflow a no-op.
    customerName: {
        fontWeight: 600,
        color: "#2b3648",
        minWidth: 0,
        maxWidth: 190,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    // Counts as pills rather than bare digits - a zero reads as muted, a real
    // count reads as filled, so the busy rows stand out at a glance.
    countPill: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 26,
        height: 22,
        padding: "0 8px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 800,
        lineHeight: 1,
    },
    // Date over time, so the column stays scannable by day without losing the
    // detail that matters when two nudges land on the same date.
    dateStack: { lineHeight: 1.25 },
    dateMain: { fontSize: 12.5, fontWeight: 600, color: "#2b3648" },
    dateSub: { fontSize: 11, color: "#9aa4b4" },
    stageChip: {
        display: "inline-block",
        padding: "3px 9px",
        borderRadius: 7,
        background: "#EEF2F7",
        border: "1px solid #e1e8f2",
        color: "#5c6779",
        fontSize: 11.5,
        fontWeight: 600,
    },
    emptyState: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 8,
        padding: "48px 0",
        color: "#9aa4b4",
        fontSize: 13.5,
    },
    emptyIcon: { fontSize: 40, color: "#cbd3e0" },
    loaderWrap: { display: "flex", justifyContent: "center", alignItems: "center", padding: "80px 0" },

    // Reserved slot rather than a conditional element, so content doesn't shift
    // by 4px every time a fetch starts.
    progressSlot: { height: 4, marginBottom: 10, borderRadius: 999, overflow: "hidden" },
    // Mirrors the banner on the AI Review / Experience Centers / Locations
    // pages, tinted to this page's WhatsApp green instead of the orange.
    loadingBanner: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "9px 14px",
        marginBottom: 14,
        borderRadius: 12,
        background: "#e8f6f2",
        border: `1px solid ${WA}33`,
    },
    loadingBannerTitle: {
        fontSize: 13,
        fontWeight: 700,
        color: WA_DARK,
    },
    loadingBannerHint: {
        fontSize: 11.5,
        color: "#5f7a73",
        marginTop: 1,
    },
    // Rows keep their numbers visible while refreshing, just dimmed.
    staleWrap: { transition: "opacity .25s ease" },

    // Funnel/outcome row rebuilt around a real determinate bar instead of a
    // background gradient - the bar animates between values, so changing the
    // date range reads as the numbers moving rather than redrawing.
    meterRow: {
        padding: "10px 12px",
        borderRadius: 11,
        border: "1px solid #eaeff6",
        background: "#fff",
        marginBottom: 8,
        transition: "transform .15s ease, box-shadow .15s ease, border-color .15s ease",
        "&:last-child": { marginBottom: 0 },
        "&:hover": { transform: "translateX(2px)", boxShadow: "0 3px 10px rgba(28,55,93,0.09)" },
    },
    meterRowInteractive: { cursor: "pointer" },
    meterRowActive: { borderColor: WA, boxShadow: `0 0 0 2px ${WA}22` },
    meterHead: { display: "flex", alignItems: "center", gap: 9, marginBottom: 7 },
    meterDot: { width: 10, height: 10, borderRadius: "50%", flexShrink: 0 },
    meterLabel: { fontSize: 13, fontWeight: 600, color: "#4a5568", flex: 1 },
    meterValue: { fontSize: 14, fontWeight: 800, color: "#1f2d3d" },
    meterPct: { fontSize: 11.5, color: "#8b93a6", minWidth: 46, textAlign: "right", fontWeight: 700 },

    statMeterWrap: { marginTop: 9 },
    statMeterCaption: {
        display: "flex",
        justifyContent: "space-between",
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: 0.3,
        color: "#9aa4b4",
        marginTop: 5,
        textTransform: "uppercase",
    },

    tableToolbar: {
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 10,
        marginBottom: 12,
    },
    searchField: {
        minWidth: 260,
        "& .MuiOutlinedInput-root": { borderRadius: 999, backgroundColor: "#fff" },
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: WA },
    },
    downloadButton: {
        borderRadius: "999px !important",
        fontWeight: 600,
        display: "inline-flex !important",
        alignItems: "center !important",
        color: "#fff !important",
        background: `linear-gradient(135deg, ${WA_DARK}, ${WA}) !important`,
        borderColor: "transparent !important",
        boxShadow: "0 4px 10px rgba(11,107,88,0.3)",
    },
    leadLink: {
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "3px 9px",
        borderRadius: 7,
        background: "#E8F5F1",
        color: WA_DARK,
        fontWeight: 700,
        fontSize: 12.5,
        cursor: "pointer",
        transition: "background-color .15s ease",
        "&:hover": { background: "#d3ebe4" },
    },
    stickyHead: { position: "sticky", top: 0, zIndex: 2 },
    // Matches a real two-line row (date over time), so the table doesn't
    // shrink when the skeletons are replaced.
    skeletonCell: { ...cellBorder, height: 52 },
}));

const PRESETS = [
    { label: "7d", days: 7 },
    { label: "30d", days: 30 },
    { label: "90d", days: 90 },
];

const formatNumber = (n) => (n == null ? "—" : Number(n).toLocaleString("en-IN"));

// Reply latency reads better rescaled than as a raw hour count - 0.4h and
// 96h are both unhelpful as "hours".
const formatDuration = (hours) => {
    if (hours == null) return "—";
    if (hours < 1) return `${Math.round(hours * 60)}m`;
    if (hours < 48) return `${Math.round(hours * 10) / 10}h`;
    return `${Math.round((hours / 24) * 10) / 10}d`;
};

// Deterministic colour per customer, so the same person keeps the same badge
// across refreshes and date ranges instead of flickering between colours.
const AVATAR_COLORS = ["#0d9488", "#6366f1", "#d97706", "#db2777", "#2563eb", "#65a30d", "#c2410c", "#7c3aed"];
const avatarFor = (name) => {
    const text = String(name || "").trim();
    const initials = text
        ? text.split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase()
        : "?";
    let hash = 0;
    for (let i = 0; i < text.length; i += 1) hash = (hash * 31 + text.charCodeAt(i)) >>> 0;
    return { initials, color: AVATAR_COLORS[hash % AVATAR_COLORS.length] };
};

// First-load placeholder. Shaped like the real dashboard - six tiles, a wide
// chart and a side panel - so the layout is already in place when the data
// lands, instead of a lone spinner collapsing the page and then expanding it.
const SkeletonDashboard = ({ classes }) => (
    <>
        <Grid container spacing={2}>
            {Array.from({ length: 6 }).map((_, i) => (
                <Grid item xs={12} sm={6} md={4} lg={2} key={i}>
                    <Card className={classes.statCard} elevation={0}>
                        <div className={classes.statAccent} style={{ background: "#e3e9f2" }} />
                        <CardContent className={classes.statContent}>
                            <div className={classes.statTop}>
                                <Skeleton variant="rect" width={28} height={28} style={{ borderRadius: 9 }} />
                                <Skeleton variant="text" width="55%" height={14} />
                            </div>
                            <Skeleton variant="text" width="45%" height={34} />
                            <Skeleton variant="text" width="80%" height={12} />
                            <Skeleton variant="rect" height={8} style={{ borderRadius: 999, marginTop: 9 }} />
                        </CardContent>
                    </Card>
                </Grid>
            ))}
        </Grid>
        <Grid container spacing={2} style={{ marginTop: 2 }}>
            {/* Wide slot: the outcome donut beside its legend - matching the
                side-by-side layout the real panel uses now that the daily bar
                chart is gone. */}
            <Grid item xs={12} md={7}>
                <Card className={classes.panelCard} elevation={0}>
                    <CardContent>
                        <Skeleton variant="text" width={220} height={22} />
                        <Skeleton variant="text" width={340} height={14} />
                        <div className={classes.progressSlot}><CardLinearProgress /></div>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={5}>
                                <div className={classes.donutWrapWide} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <Skeleton variant="circle" width={200} height={200} />
                                </div>
                            </Grid>
                            <Grid item xs={12} sm={7}>
                                {/* Four rows: Real, Hold, Junk, Not applied. */}
                                {Array.from({ length: 4 }).map((_, i) => (
                                    <Skeleton key={i} variant="rect" height={58} style={{ borderRadius: 11, marginBottom: 8 }} />
                                ))}
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Grid>
            {/* Narrow slot: the three Conversion steps. */}
            <Grid item xs={12} md={5}>
                <Card className={classes.panelCard} elevation={0}>
                    <CardContent>
                        <Skeleton variant="text" width={140} height={22} />
                        <Skeleton variant="text" width={200} height={14} />
                        <div className={classes.progressSlot}><CardLinearProgress /></div>
                        {Array.from({ length: 3 }).map((_, i) => (
                            <Skeleton key={i} variant="rect" height={58} style={{ borderRadius: 11, marginBottom: 8 }} />
                        ))}
                    </CardContent>
                </Card>
            </Grid>
        </Grid>

        {/* The jump-to-table strip. */}
        <Skeleton variant="rect" height={50} style={{ borderRadius: 12, marginTop: 14 }} />

        {/* The table card itself. Without this the first load ended at the
            panels and the whole table popped in afterwards, which is the jump
            the skeletons exist to avoid. */}
        <Card className={classes.tableCard} elevation={0}>
            <CardContent>
                <Skeleton variant="text" width={150} height={22} />
                <Skeleton variant="text" width={330} height={14} />
                <div className={classes.tableToolbar}>
                    <Skeleton variant="rect" width={260} height={38} style={{ borderRadius: 999 }} />
                    <Skeleton variant="text" width={120} />
                    <Skeleton variant="rect" width={160} height={34} style={{ borderRadius: 999, marginLeft: "auto" }} />
                </div>
                <Skeleton variant="rect" height={34} style={{ borderRadius: 9, marginBottom: 12 }} />
                <div className={classes.progressSlot}><CardLinearProgress /></div>
                <TableContainer style={{ border: "1px solid #eaeff6", borderRadius: 12 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {TABLE_COLUMNS.map((col) => (
                                    <TableCell key={col.label} className={classes.headCell} align={col.align || "inherit"}>
                                        {col.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <SkeletonRows classes={classes} rows={6} />
                        </TableBody>
                    </Table>
                </TableContainer>
            </CardContent>
        </Card>
    </>
);

// Placeholder rows matching the ten real columns, so the table keeps its width
// and row rhythm during a refetch.
const SkeletonRows = ({ classes, rows = 8 }) => (
    <>
        {Array.from({ length: rows }).map((_, i) => (
            <TableRow key={i}>
                <TableCell className={classes.skeletonCell}><Skeleton variant="rect" width={70} height={20} style={{ borderRadius: 7 }} /></TableCell>
                <TableCell className={classes.skeletonCell}><Skeleton variant="text" width="70%" /></TableCell>
                <TableCell className={classes.skeletonCell} align="right"><Skeleton variant="text" width={24} style={{ marginLeft: "auto" }} /></TableCell>
                <TableCell className={classes.skeletonCell} align="right"><Skeleton variant="text" width={24} style={{ marginLeft: "auto" }} /></TableCell>
                <TableCell className={classes.skeletonCell}><Skeleton variant="text" width={86} /></TableCell>
                <TableCell className={classes.skeletonCell}><Skeleton variant="text" width={86} /></TableCell>
                {/* Two lines each: these columns render a date over a time in
                    the real table, so a single line here would make the
                    skeleton rows visibly shorter than what replaces them. */}
                <TableCell className={classes.skeletonCell}>
                    <Skeleton variant="text" width={92} />
                    <Skeleton variant="text" width={60} height={11} />
                </TableCell>
                <TableCell className={classes.skeletonCell}>
                    <Skeleton variant="text" width={92} />
                    <Skeleton variant="text" width={104} height={11} />
                </TableCell>
                <TableCell className={classes.skeletonCell}><Skeleton variant="rect" width={78} height={22} style={{ borderRadius: 7 }} /></TableCell>
                <TableCell className={classes.skeletonCell}><Skeleton variant="text" width={86} /></TableCell>
            </TableRow>
        ))}
    </>
);

const BotWotWhatsAppDashboard = ({ setSnackInfo }) => {
    const classes = useStyles();
    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [applied, setApplied] = useState(DEFAULT_FILTERS);
    const [activePreset, setActivePreset] = useState(30);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);
    // "Updated 4 minutes ago" is only true at the moment it renders, so a slow
    // tick re-renders it. 30s is fine - moment rounds to the minute anyway.
    const [nowTick, setNowTick] = useState(Date.now());
    useEffect(() => {
        const id = setInterval(() => setNowTick(Date.now()), 30000);
        return () => clearInterval(id);
    }, []);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [search, setSearch] = useState("");
    // Clicking an outcome row in the donut legend filters the table to it -
    // the chart becomes a control rather than just a picture.
    const [outcomeFilter, setOutcomeFilter] = useState(null);
    const tableRef = useRef(null);
    // The lead whose WhatsApp thread is open in the side drawer.
    const [chatLead, setChatLead] = useState(null);

    const scrollToTable = () => {
        tableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const fetchStats = useCallback(async (range) => {
        setLoading(true);
        try {
            const res = await HttpService.getBotWotWhatsAppStats(range);
            if (!res.data?.success) throw new Error(res.data?.message || "Failed to load");
            setStats(res.data);
            setLastUpdated(moment());
        } catch (error) {
            setStats(null);
            setSnackInfo(
                error?.response?.data?.message || error.message || "Could not load the WhatsApp funnel",
                "error"
            );
        }
        setLoading(false);
    }, [setSnackInfo]);

    useEffect(() => {
        fetchStats(applied);
        setPage(0);
    }, [applied, fetchStats]);

    const applyPreset = (days) => {
        const next = {
            from: moment().subtract(days - 1, "days").format("YYYY-MM-DD"),
            to: moment().format("YYYY-MM-DD"),
        };
        setActivePreset(days);
        setFilters(next);
        setApplied(next);
    };

    const totals = stats?.totals;
    const leads = useMemo(() => stats?.leads || [], [stats]);

    const outcomeRows = useMemo(() => {
        const outcomes = stats?.outcomes || {};
        const replied = totals?.leadsReplied || 0;
        return OUTCOME_ORDER.map((key) => {
            const count = outcomes[key] || 0;
            return {
                key,
                ...OUTCOME_META[key],
                count,
                pct: replied ? Math.round((count / replied) * 1000) / 10 : 0,
            };
        });
    }, [stats, totals]);

    const donutData = useMemo(() => ({
        labels: outcomeRows.map((r) => r.label),
        datasets: [
            {
                data: outcomeRows.map((r) => r.count),
                backgroundColor: outcomeRows.map((r) => r.color),
                borderWidth: 2,
                borderColor: "#fff",
                hoverOffset: 6,
            },
        ],
    }), [outcomeRows]);

    const donutOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        cutout: "62%",
        plugins: {
            legend: { display: false },
            tooltip: { callbacks: { label: (ctx) => ` ${ctx.label}: ${ctx.parsed}` } },
        },
    }), []);

    const funnelRows = useMemo(() => {
        const texted = totals?.leadsTexted || 0;
        const pct = (n) => (texted ? Math.round((n / texted) * 1000) / 10 : 0);
        return [
            { key: "texted", label: "Texted", count: texted, pct: 100, color: SENT_COLOR },
            { key: "replied", label: "Replied", count: totals?.leadsReplied || 0, pct: pct(totals?.leadsReplied || 0), color: REPLIED_COLOR },
            { key: "silent", label: "No reply yet", count: totals?.leadsAwaitingReply || 0, pct: pct(totals?.leadsAwaitingReply || 0), color: NEUTRAL_COLOR },
        ];
    }, [totals]);

    // `meter` turns a tile into a small determinate bar: a share worth seeing as
    // a proportion rather than only as a number. Tiles with no meaningful
    // denominator (median reply time) deliberately have none.
    const statTiles = totals
        ? [
            { label: "Leads texted", value: formatNumber(totals.leadsTexted), hint: `${formatNumber(totals.messagesSent)} messages sent`, color: SENT_COLOR, Icon: SendOutlinedIcon },
            {
                label: "Leads replied", value: formatNumber(totals.leadsReplied), hint: `${formatNumber(totals.messagesReceived)} replies received`, color: REPLIED_COLOR, Icon: ReplyOutlinedIcon,
                meter: { value: totals.replyRate, left: "of texted leads", right: `${totals.replyRate}%` },
            },
            {
                label: "Reply rate", value: `${totals.replyRate}%`, hint: `${formatNumber(totals.leadsAwaitingReply)} still silent`, color: WA_LIGHT, Icon: TrendingUpIcon,
                meter: { value: totals.replyRate, left: "replied", right: `${Math.round((100 - totals.replyRate) * 10) / 10}% silent` },
            },
            { label: "Median reply time", value: formatDuration(totals.medianResponseHours), hint: "first text → first reply", color: "#6366f1", Icon: TimerOutlinedIcon },
            {
                label: "Failed sends", value: formatNumber(totals.failedSends), hint: `${totals.sendFailureRate}% of send attempts`, color: JUNK_COLOR, Icon: ErrorOutlineIcon,
                meter: { value: totals.sendFailureRate, left: "of send attempts", right: `${totals.sendFailureRate}%` },
            },
            {
                label: "Nudged again", value: formatNumber(totals.leadsTextedMoreThanOnce), hint: "texted more than once", color: HOLD_COLOR, Icon: RepeatOutlinedIcon,
                meter: {
                    value: totals.leadsTexted ? (totals.leadsTextedMoreThanOnce / totals.leadsTexted) * 100 : 0,
                    left: "of texted leads",
                    right: `${totals.leadsTexted ? Math.round((totals.leadsTextedMoreThanOnce / totals.leadsTexted) * 1000) / 10 : 0}%`,
                },
            },
        ]
        : [];

    // Outcome chip + free-text search, applied together. A lead with no reply
    // has no outcome, so the outcome filter excludes it rather than bucketing
    // it under "Not applied" - which is a real verdict, not an absence of one.
    const filteredLeads = useMemo(() => {
        let rows = leads;
        if (outcomeFilter) {
            rows = rows.filter((l) => l.replyCount > 0 && (l.outcome || "Not applied") === outcomeFilter);
        }
        const term = search.trim().toLowerCase();
        if (term) {
            rows = rows.filter(
                (l) =>
                    String(l.leadNo || "").toLowerCase().includes(term) ||
                    String(l.uniqueLeadNumber || "").toLowerCase().includes(term) ||
                    String(l.customerName || "").toLowerCase().includes(term) ||
                    String(l.currentStage || "").toLowerCase().includes(term)
            );
        }
        return rows;
    }, [leads, outcomeFilter, search]);

    useEffect(() => {
        setPage(0);
    }, [search, outcomeFilter]);

    const pagedLeads = filteredLeads.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    // Exports everything matching the current filters, not just the page on
    // screen, and includes the counts the table only hints at.
    const downloadExcel = () => {
        if (!filteredLeads.length) {
            setSnackInfo("No data available to download", "info");
            return;
        }
        const rows = filteredLeads.map((l, i) => ({
            "Sl. No.": i + 1,
            "Lead No.": l.leadNo || "-",
            "Unique Lead No.": l.uniqueLeadNumber || "-",
            "Customer": l.customerName || "-",
            "Lead Created": l.leadCreatedAt ? moment(l.leadCreatedAt).format("DD-MM-YYYY") : "-",
            "Lead Assigned": l.leadAssignedDate ? moment(l.leadAssignedDate).format("DD-MM-YYYY") : "-",
            "Texts Sent": l.sentCount,
            "Failed Sends": l.failedSendCount || 0,
            "Replies": l.replyCount,
            "First Texted": l.firstSentAt ? moment(l.firstSentAt).format("DD-MM-YYYY hh:mm A") : "-",
            "First Reply": l.firstRepliedAt ? moment(l.firstRepliedAt).format("DD-MM-YYYY hh:mm A") : "-",
            "Reply Time (hrs)":
                l.firstSentAt && l.firstRepliedAt
                    ? Math.round(((new Date(l.firstRepliedAt) - new Date(l.firstSentAt)) / 36e5) * 100) / 100
                    : "-",
            "Outcome": l.replyCount ? l.outcome || "Not applied" : "No reply yet",
            "Current Stage": l.currentStage || "-",
        }));
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "WhatsApp Funnel");
        XLSX.writeFile(wb, `BotWot_WhatsApp_Funnel_${moment().format("YYYYMMDD_HHmmss")}.xlsx`);
    };

    return (
        <div className={classes.pageRoot}>
            <div className={classes.pageHeaderBanner}>
                <div className={classes.pageHeaderIconWrap}>
                    <WhatsAppIcon fontSize="large" />
                </div>
                <div>
                    <div className={classes.pageHeaderTitle}>BotWot WhatsApp Funnel</div>
                    <div className={classes.pageHeaderSubtitle}>
                        Leads nudged on WhatsApp when an AI call left details missing, and how many wrote back
                    </div>
                </div>
                <div className={classes.pageHeaderRight}>
                    <div className={classes.pageHeaderChip}>
                        <DateRangeOutlinedIcon style={{ fontSize: 15 }} />
                        {moment(applied.from).format("DD MMM")} — {moment(applied.to).format("DD MMM YYYY")}
                        <span style={{ opacity: 0.7, fontWeight: 600 }}>
                            {`· ${moment(applied.to).diff(moment(applied.from), "days") + 1}d`}
                        </span>
                    </div>
                    {/* A row, matching the AI review dashboard: the control and the
                        freshness it reports read as one unit side by side. */}
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <MuiTooltip
                            title={
                                lastUpdated
                                    ? `Last pulled at ${lastUpdated.format("DD MMM YYYY, hh:mm:ss A")} — click to fetch the latest figures`
                                    : "Fetch the latest figures"
                            }
                        >
                            {/* A plain button rather than an IconButton: the word
                                "Refresh" is what makes the control findable, and a
                                tooltip alone only helps people who already hovered.
                                The span is required because a disabled button fires
                                no mouse events, so the tooltip would die mid-refresh. */}
                            <span style={{ display: "inline-flex" }}>
                                <button
                                    type="button"
                                    className={classes.refreshButton}
                                    onClick={() => fetchStats(applied)}
                                    disabled={loading}
                                >
                                    <RefreshIcon className={loading ? classes.refreshSpinning : ""} style={{ fontSize: 17 }} />
                                    {loading ? "Refreshing…" : "Refresh"}
                                </button>
                            </span>
                        </MuiTooltip>
                        {lastUpdated && (
                            <div className={classes.lastUpdatedText}>
                                <span className={classes.liveDot} />
                                {/* Relative beats "02:14 PM": it answers "is this
                                    stale?" without the reader doing the arithmetic. */}
                                {loading ? "Fetching latest…" : `Updated ${moment(lastUpdated).from(nowTick)}`}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <Paper className={classes.filterPaper} elevation={0}>
                <div className={classes.filterHeader}>
                    <TuneIcon fontSize="small" style={{ color: WA }} /> Filters
                </div>
                <div className={classes.filterRow}>
                    <TextField
                        className={classes.field}
                        size="small"
                        variant="outlined"
                        label="From"
                        type="date"
                        value={filters.from}
                        InputLabelProps={{ shrink: true }}
                        onChange={(e) => { setFilters({ ...filters, from: e.target.value }); setActivePreset(null); }}
                    />
                    <TextField
                        className={classes.field}
                        size="small"
                        variant="outlined"
                        label="To"
                        type="date"
                        value={filters.to}
                        InputLabelProps={{ shrink: true }}
                        onChange={(e) => { setFilters({ ...filters, to: e.target.value }); setActivePreset(null); }}
                    />
                    <Button type="primary" className={classes.applyButton} onClick={() => setApplied(filters)}>
                        Apply
                    </Button>
                    <div className={classes.presetGroup}>
                        {PRESETS.map((p) => (
                            <Chip
                                key={p.days}
                                size="small"
                                label={p.label}
                                className={classes.presetChip}
                                onClick={() => applyPreset(p.days)}
                                style={
                                    activePreset === p.days
                                        ? { background: WA, color: "#fff" }
                                        : { background: "#fff", border: "1px solid #dde4ee", color: "#6b7689" }
                                }
                            />
                        ))}
                    </div>
                </div>
            </Paper>

            {/* One page-level banner rather than one per card. Each card and the
                table already show their own thin bar; this is the single place
                that says WHAT is loading, which matters most on a refetch - the
                old numbers stay on screen (dimmed) then, so without it there is
                nothing to explain why they have not changed yet. */}
            {loading && (
                <div className={classes.loadingBanner}>
                    <CircularProgress size={17} thickness={5} style={{ color: WA_DARK }} />
                    <div style={{ minWidth: 0 }}>
                        <div className={classes.loadingBannerTitle}>Fetching WhatsApp activity…</div>
                        <div className={classes.loadingBannerHint}>
                            {totals
                                ? "Reloading for the selected range — the figures below stay visible until the new data lands."
                                : "Pulling every BotWot WhatsApp conversation in the selected range."}
                        </div>
                    </div>
                </div>
            )}

            {/* Only the FIRST load shows skeletons. A refetch keeps the previous
                numbers on screen, dimmed, with a bar on each card - the layout
                never collapses and you can still read the old figures. */}
            {loading && !totals ? (
                <SkeletonDashboard classes={classes} />
            ) : !totals ? (
                <Paper className={classes.panelCard} elevation={0}>
                    <div className={classes.emptyState}>
                        <InboxOutlinedIcon className={classes.emptyIcon} />
                        Could not load the funnel for this range.
                    </div>
                </Paper>
            ) : (
                <>
                    <Grid
                        container
                        spacing={2}
                        className={classes.staleWrap}
                        style={{ opacity: loading ? 0.5 : 1 }}
                    >
                        {statTiles.map((tile) => (
                            <Grid item xs={12} sm={6} md={4} lg={2} key={tile.label}>
                                <Card className={classes.statCard} elevation={0}>
                                    <div
                                        className={classes.statAccent}
                                        style={{ background: `linear-gradient(180deg, ${tile.color}, ${tile.color}66)` }}
                                    />
                                    <CardContent className={classes.statContent}>
                                        <div className={classes.statTop}>
                                            <span className={classes.statIconWrap} style={{ background: `${tile.color}16` }}>
                                                <tile.Icon style={{ fontSize: 16, color: tile.color }} />
                                            </span>
                                            <span className={classes.statLabel}>{tile.label}</span>
                                        </div>
                                        <div className={classes.statValue}>{tile.value}</div>
                                        <div className={classes.statHint}>{tile.hint}</div>
                                        {/* A share is easier to judge as a bar than
                                            as a second number in the hint line. */}
                                        {tile.meter ? (
                                            <div className={classes.statMeterWrap}>
                                                <MeterBar value={tile.meter.value} color={tile.color} height={6} />
                                                <div className={classes.statMeterCaption}>
                                                    <span>{tile.meter.left}</span>
                                                    <span style={{ color: tile.color }}>{tile.meter.right}</span>
                                                </div>
                                            </div>
                                        ) : null}
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                    <Grid container spacing={2} style={{ marginTop: 2 }}>
                        {/* Outcome split now takes the wide slot the daily bar
                            chart used to hold: with the room, the donut and its
                            legend sit side by side instead of stacked. */}
                        <Grid item xs={12} md={7}>
                            <Card className={classes.panelCard} elevation={0}>
                                <CardContent>
                                    <div className={classes.panelTitle}>What replies did to the lead</div>
                                    <div className={classes.panelHint}>
                                        Outcome recorded by /botwot-customer-response — click one to filter the lead table below.
                                    </div>
                                    <div className={classes.progressSlot}>{loading ? <CardLinearProgress /> : null}</div>
                                    {totals.leadsReplied ? (
                                        <Grid
                                            container
                                            spacing={2}
                                            alignItems="center"
                                            className={classes.staleWrap}
                                            style={{ opacity: loading ? 0.5 : 1 }}
                                        >
                                            <Grid item xs={12} sm={5}>
                                                <div className={classes.donutWrapWide}>
                                                    <Doughnut data={donutData} options={donutOptions} />
                                                    {/* Centre read-out: the donut's own total, so the
                                                        denominator behind every percentage is visible
                                                        without hovering a segment. */}
                                                    <div className={classes.donutCenter}>
                                                        <div className={classes.donutCenterValue}>
                                                            {formatNumber(totals.leadsReplied)}
                                                        </div>
                                                        <div className={classes.donutCenterLabel}>Replied</div>
                                                    </div>
                                                </div>
                                            </Grid>
                                            <Grid item xs={12} sm={7}>
                                                {outcomeRows.map((row) => {
                                                    const active = outcomeFilter === row.key;
                                                    return (
                                                        <div
                                                            key={row.key}
                                                            role="button"
                                                            tabIndex={0}
                                                            // Clicking the active row clears the filter, so the
                                                            // legend toggles rather than trapping the table in a
                                                            // filter with no obvious way back.
                                                            onClick={() => setOutcomeFilter(active ? null : row.key)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === "Enter" || e.key === " ") {
                                                                    e.preventDefault();
                                                                    setOutcomeFilter(active ? null : row.key);
                                                                }
                                                            }}
                                                            className={`${classes.meterRow} ${classes.meterRowInteractive} ${active ? classes.meterRowActive : ""}`}
                                                        >
                                                            <div className={classes.meterHead}>
                                                                <span className={classes.meterDot} style={{ background: row.color }} />
                                                                <span className={classes.meterLabel}>{row.label}</span>
                                                                <span className={classes.meterValue}>{formatNumber(row.count)}</span>
                                                                <span className={classes.meterPct}>{row.pct}%</span>
                                                            </div>
                                                            <MeterBar value={row.pct} color={row.color} />
                                                        </div>
                                                    );
                                                })}
                                            </Grid>
                                        </Grid>
                                    ) : (
                                        <div className={classes.emptyState} style={{ padding: "26px 0" }}>
                                            No replies in this range.
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={5}>
                            <Card className={classes.panelCard} elevation={0}>
                                <CardContent>
                                    <div className={classes.panelTitle}>Conversion</div>
                                    <div className={classes.panelHint}>Distinct leads, not messages.</div>
                                    <div className={classes.progressSlot}>{loading ? <CardLinearProgress /> : null}</div>
                                    <div className={classes.staleWrap} style={{ opacity: loading ? 0.5 : 1 }}>
                                        {funnelRows.map((row) => (
                                            <div key={row.key} className={classes.meterRow}>
                                                <div className={classes.meterHead}>
                                                    <span className={classes.meterDot} style={{ background: row.color }} />
                                                    <span className={classes.meterLabel}>{row.label}</span>
                                                    <span className={classes.meterValue}>{formatNumber(row.count)}</span>
                                                    <span className={classes.meterPct}>{row.pct}%</span>
                                                </div>
                                                <MeterBar value={row.pct} color={row.color} />
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Without the tall bar chart the table sits further down than
                        it looks - this strip says it is there and jumps to it. */}
                    <div
                        role="button"
                        tabIndex={0}
                        className={classes.jumpToTable}
                        onClick={scrollToTable}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                scrollToTable();
                            }
                        }}
                    >
                        <span className={classes.jumpToTableText}>
                            {`Lead activity — all ${formatNumber(leads.length)} lead${leads.length === 1 ? "" : "s"} with WhatsApp activity, in the table below`}
                        </span>
                        <span className={classes.jumpToTableCta}>
                            View table
                            <KeyboardArrowDownIcon style={{ fontSize: 17 }} />
                        </span>
                    </div>

                    <Card className={classes.tableCard} elevation={0} ref={tableRef}>
                        <CardContent>
                            <div className={classes.panelTitle}>Lead activity</div>
                            <div className={classes.panelHint} style={{ marginBottom: 10 }}>
                                Every lead with WhatsApp activity in this range, most recent first.
                            </div>
                            <div className={classes.tableToolbar}>
                                <TextField
                                    size="small"
                                    variant="outlined"
                                    placeholder="Search lead no, customer, stage…"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className={classes.searchField}
                                    disabled={loading}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon style={{ fontSize: 18, color: "#9aa4b4" }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: search ? (
                                            <InputAdornment position="end">
                                                <IconButton size="small" onClick={() => setSearch("")}>
                                                    <ClearIcon style={{ fontSize: 15 }} />
                                                </IconButton>
                                            </InputAdornment>
                                        ) : null,
                                    }}
                                />
                                {outcomeFilter ? (
                                    <Chip
                                        size="small"
                                        label={`Outcome: ${OUTCOME_META[outcomeFilter]?.label || outcomeFilter}`}
                                        onDelete={() => setOutcomeFilter(null)}
                                        style={{
                                            fontWeight: 700,
                                            color: OUTCOME_META[outcomeFilter]?.color,
                                            background: `${OUTCOME_META[outcomeFilter]?.color}14`,
                                            border: `1px solid ${OUTCOME_META[outcomeFilter]?.color}59`,
                                        }}
                                    />
                                ) : null}
                                <span style={{ fontSize: 12.5, color: "#8b93a6", fontWeight: 600 }}>
                                    {`${formatNumber(filteredLeads.length)} of ${formatNumber(leads.length)} leads`}
                                </span>
                                <Button
                                    icon={<GetAppIcon style={{ fontSize: 16, marginRight: 6 }} />}
                                    onClick={downloadExcel}
                                    className={classes.downloadButton}
                                    disabled={loading}
                                    style={{ marginLeft: "auto" }}
                                >
                                    Download Excel
                                </Button>
                            </div>
                            <div className={classes.tableHint}>
                                <ErrorOutlineIcon style={{ fontSize: 15 }} />
                                Click any row to read that customer's WhatsApp conversation. “Texts” counts successful
                                sends — a lead can be nudged on more than one day, and a red marker means at least one
                                send to that lead failed. Click the <span style={{ color: WA_DARK, fontWeight: 700 }}>lead
                                number</span> to open the lead page instead.
                            </div>
                            <div className={classes.progressSlot}>{loading ? <CardLinearProgress /> : null}</div>

                            {!loading && filteredLeads.length === 0 ? (
                                <div className={classes.emptyState}>
                                    <InboxOutlinedIcon className={classes.emptyIcon} />
                                    {search || outcomeFilter
                                        ? "No leads match this filter."
                                        : "No WhatsApp activity in this range."}
                                </div>
                            ) : (
                                <>
                                    <TableContainer style={{ maxHeight: 540, border: "1px solid #eaeff6", borderRadius: 12 }}>
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    {TABLE_COLUMNS.map((col) => (
                                                        <TableCell
                                                            key={col.label}
                                                            className={`${classes.headCell} ${classes.stickyHead}`}
                                                            align={col.align || "inherit"}
                                                        >
                                                            {col.label}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {loading ? <SkeletonRows classes={classes} rows={Math.min(rowsPerPage, 8)} /> : null}
                                                {!loading && pagedLeads.map((lead, idx) => {
                                                    const outcome = lead.replyCount ? (lead.outcome || "Not applied") : null;
                                                    const meta = outcome ? OUTCOME_META[outcome] : null;
                                                    return (
                                                        <TableRow
                                                            key={lead.leadId || idx}
                                                            className={classes.bodyRow}
                                                            hover
                                                            // Rows with no leadId are replies we couldn't match to a
                                                            // lead - there is no thread to fetch for those.
                                                            style={{ cursor: lead.leadId ? "pointer" : "default" }}
                                                            onClick={() => lead.leadId && setChatLead(lead)}
                                                        >
                                                            <TableCell className={classes.bodyCell} style={{ fontWeight: 600 }}>
                                                                {/* Replies we couldn't match to a lead have no
                                                                    leadId, so there is nothing to open - those
                                                                    stay plain text rather than a dead link. */}
                                                                {lead.leadId ? (
                                                                    <MuiTooltip title="Open this lead's page in a new tab">
                                                                        <span
                                                                            className={classes.leadLink}
                                                                            // Stops the row's own click from also opening
                                                                            // the chat drawer behind the new tab.
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                window.open(`/project-logs/${lead.leadId}`, "_blank", "noopener,noreferrer");
                                                                            }}
                                                                        >
                                                                            {lead.leadNo || lead.uniqueLeadNumber || "—"}
                                                                            <OpenInNewIcon style={{ fontSize: 11 }} />
                                                                        </span>
                                                                    </MuiTooltip>
                                                                ) : (
                                                                    lead.leadNo || lead.uniqueLeadNumber || "—"
                                                                )}
                                                            </TableCell>
                                                            <TableCell className={classes.bodyCell}>
                                                                <span className={classes.customerCell}>
                                                                    <span
                                                                        className={classes.avatar}
                                                                        style={{ background: avatarFor(lead.customerName).color }}
                                                                    >
                                                                        {avatarFor(lead.customerName).initials}
                                                                    </span>
                                                                    {/* Tooltip unconditionally, rather than guessing
                                                                        from character count whether this particular
                                                                        name truncates - a wrong guess would hide part
                                                                        of a name with no way to read it. */}
                                                                    <MuiTooltip title={lead.customerName || ""}>
                                                                        <span className={classes.customerName}>
                                                                            {lead.customerName || "—"}
                                                                        </span>
                                                                    </MuiTooltip>
                                                                </span>
                                                            </TableCell>
                                                            <TableCell className={classes.bodyCell} align="right">
                                                                <span
                                                                    className={classes.countPill}
                                                                    style={{
                                                                        color: lead.sentCount ? SENT_COLOR : "#b6bdc9",
                                                                        background: lead.sentCount ? `${SENT_COLOR}18` : "#F3F5F8",
                                                                    }}
                                                                >
                                                                    {lead.sentCount}
                                                                </span>
                                                                {lead.sendFailed && (
                                                                    <MuiTooltip
                                                                        title={`${lead.failedSendCount || 1} send(s) to this lead failed`}
                                                                    >
                                                                        <span
                                                                            className={classes.countPill}
                                                                            style={{ color: JUNK_COLOR, background: `${JUNK_COLOR}18`, marginLeft: 4 }}
                                                                        >
                                                                            !
                                                                        </span>
                                                                    </MuiTooltip>
                                                                )}
                                                            </TableCell>
                                                            <TableCell className={classes.bodyCell} align="right">
                                                                {/* Doubles as the affordance for the row's chat drawer -
                                                                    a bare number gave no hint the thread was readable. */}
                                                                <MuiTooltip
                                                                    title={
                                                                        lead.leadId
                                                                            ? lead.replyCount
                                                                                ? `Read the ${lead.replyCount} repl${lead.replyCount === 1 ? "y" : "ies"} on WhatsApp`
                                                                                : "Open this lead's WhatsApp thread"
                                                                            : "No lead linked to this conversation"
                                                                    }
                                                                >
                                                                    <span
                                                                        className={classes.countPill}
                                                                        style={{
                                                                            gap: 4,
                                                                            cursor: lead.leadId ? "pointer" : "default",
                                                                            color: lead.replyCount ? REPLIED_COLOR : "#b6bdc9",
                                                                            background: lead.replyCount ? `${REPLIED_COLOR}18` : "#F3F5F8",
                                                                        }}
                                                                    >
                                                                        {lead.replyCount}
                                                                        {lead.leadId ? <ChatBubbleOutlineIcon style={{ fontSize: 11 }} /> : null}
                                                                    </span>
                                                                </MuiTooltip>
                                                            </TableCell>
                                                            {/* Lead dates come from the Lead itself, not from any
                                                                WhatsApp row - a lead can be months old before it
                                                                was ever nudged. */}
                                                            <TableCell className={classes.bodyCell}>
                                                                {lead.leadCreatedAt ? (
                                                                    <span className={classes.dateMain}>
                                                                        {moment(lead.leadCreatedAt).format("DD MMM YYYY")}
                                                                    </span>
                                                                ) : (
                                                                    <span style={{ color: "#b6bdc9" }}>—</span>
                                                                )}
                                                            </TableCell>
                                                            <TableCell className={classes.bodyCell}>
                                                                {lead.leadAssignedDate ? (
                                                                    <span className={classes.dateMain}>
                                                                        {moment(lead.leadAssignedDate).format("DD MMM YYYY")}
                                                                    </span>
                                                                ) : (
                                                                    <span style={{ color: "#b6bdc9" }}>Not assigned</span>
                                                                )}
                                                            </TableCell>
                                                            <TableCell className={classes.bodyCell}>
                                                                {lead.firstSentAt ? (
                                                                    <div className={classes.dateStack}>
                                                                        <div className={classes.dateMain}>{moment(lead.firstSentAt).format("DD MMM YYYY")}</div>
                                                                        <div className={classes.dateSub}>{moment(lead.firstSentAt).format("hh:mm A")}</div>
                                                                    </div>
                                                                ) : (
                                                                    <span style={{ color: "#b6bdc9" }}>—</span>
                                                                )}
                                                            </TableCell>
                                                            <TableCell className={classes.bodyCell}>
                                                                {lead.firstRepliedAt ? (
                                                                    <div className={classes.dateStack}>
                                                                        <div className={classes.dateMain}>{moment(lead.firstRepliedAt).format("DD MMM YYYY")}</div>
                                                                        {/* How long the customer took, not just when they
                                                                            answered - the wait is the interesting part. */}
                                                                        <div className={classes.dateSub}>
                                                                            {lead.firstSentAt
                                                                                ? `${moment(lead.firstRepliedAt).format("hh:mm A")} · ${formatDuration(
                                                                                    (new Date(lead.firstRepliedAt) - new Date(lead.firstSentAt)) / 36e5
                                                                                )} later`
                                                                                : moment(lead.firstRepliedAt).format("hh:mm A")}
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <span style={{ color: "#b6bdc9" }}>Awaiting reply</span>
                                                                )}
                                                            </TableCell>
                                                            <TableCell className={classes.bodyCell}>
                                                                {meta ? (
                                                                    <Chip
                                                                        size="small"
                                                                        label={meta.label}
                                                                        className={classes.outcomeChip}
                                                                        style={{
                                                                            color: meta.color,
                                                                            background: `${meta.color}14`,
                                                                            border: `1px solid ${meta.color}59`,
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <span style={{ color: "#b6bdc9" }}>—</span>
                                                                )}
                                                            </TableCell>
                                                            <TableCell className={classes.bodyCell}>
                                                                {lead.currentStage ? (
                                                                    <span className={classes.stageChip}>{lead.currentStage}</span>
                                                                ) : (
                                                                    <span style={{ color: "#b6bdc9" }}>—</span>
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    <TablePagination
                                        component="div"
                                        count={filteredLeads.length}
                                        page={page}
                                        onPageChange={(e, p) => setPage(p)}
                                        rowsPerPage={rowsPerPage}
                                        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                                        rowsPerPageOptions={[10, 25, 50, 100]}
                                    />
                                </>
                            )}
                        </CardContent>
                    </Card>
                </>
            )}

            {/* Keyed on the lead so switching rows remounts the drawer rather
                than leaving the previous thread's scroll position and search
                behind. It fetches its own messages from the leadId. */}
            <BotWotChatDrawer
                key={chatLead?.leadId || "none"}
                open={Boolean(chatLead)}
                onClose={() => setChatLead(null)}
                leadId={chatLead?.leadId}
                leadNo={chatLead?.leadNo || chatLead?.uniqueLeadNumber}
                customerName={chatLead?.customerName}
            />
        </div>
    );
};

export default connect(null, { setSnackInfo })(BotWotWhatsAppDashboard);
